import React from "react";
import { ListItem, Box, Stack, Fade, IconButton, Tooltip } from "@mui/material";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import { ModalMenu } from "./AddMedicalCardModal";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { UserOtherPrivilegesEnum } from "src/modules/auth/userOtherPrivilegesSlice";

interface CardListProps {
  cards: ModalMenu[];
  onToggleFavorite: (card: ModalMenu) => void;
  loadingButtonId?: string | null;
  setLoadingButtonId?: (id: string | null) => void;
  onClose?: () => void;
}

const MedicalCardList: React.FC<CardListProps> = ({
  cards,
  onToggleFavorite,
  loadingButtonId,
  setLoadingButtonId,
  onClose,
}) => {
  const { t } = useTranslate();

  const renderAddButton = (card: ModalMenu) => {
    if (card.ageWarning) {
      return (
        <Tooltip title={card.ageWarning} arrow>
          <span>
            <ByzMuiAddButton
              disabled={true}
              onClick={() => { }}
              popup={false}
            >
              {t("buttons.add")}
            </ByzMuiAddButton>
          </span>
        </Tooltip>
      );
    }

    return (
      <ByzMuiAddButton
        link={card.disabled ? undefined : card.url}
        disabled={card.disabled || loadingButtonId === card.id}
        onClick={() => {
          if (card.disabled) return;
          if (setLoadingButtonId) {
            setLoadingButtonId(card.id);
            setTimeout(() => {
              if (onClose) {
                onClose();
              }
            }, 500);
          }
        }}
        popup={false}
      >
        {loadingButtonId === card.id ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                border: "2px solid",
                borderColor: "transparent",
                borderTopColor: "currentColor",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
            {t("adding")}
          </Box>
        ) : (
          t("buttons.add")
        )}
      </ByzMuiAddButton>
    );
  };

  const renderMenuItem = (card: ModalMenu, index: number) => (
    <Fade in={true} timeout={300 + index * 50} key={`${card.id}-${index}`}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: "background.paper",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        }}
      >
        <ListItem
          sx={{
            padding: "8px 20px",
            display: "flex",
            alignItems: "center",
            minHeight: 42,
          }}
        >
          {card.or_id ? (
            <IconButton
              onClick={() => onToggleFavorite(card)}
              sx={{
                marginRight: "16px",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <img
                src={
                  card.isFavorite
                    ? "/assets/icons/favorite/favorited.svg"
                    : "/assets/icons/favorite/favorite.svg"
                }
                alt="favorite"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: 40, marginRight: "16px", flexShrink: 0 }} />
          )}

          {card.image && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: "primary.lighter",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "16px",
                flexShrink: 0,
              }}
            >
              <img
                src={card.image}
                alt={card.name}
                style={{
                  width: "20px",
                  height: "20px",
                }}
              />
            </Box>
          )}

          <ByzMuiTypography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.5px",
              flex: 1,
            }}
          >
            {card.name}
          </ByzMuiTypography>

          <Tooltip title={card.disabled ? (card.disabledMessage || "") : ""} arrow placement="top">
            <Box>
              {card.or_id ? (
                <OtherPrivilegeGuard
                  uopmid={UserOtherPrivilegesEnum.MedicalCards}
                  privid={Number(card.or_id)}
                  fallback={
                    <ByzMuiAddButton
                      disabled={true}
                      onClick={() => { }}
                      popup={false}
                    />
                  }
                >
                  {renderAddButton(card)}
                </OtherPrivilegeGuard>
              ) : (
                renderAddButton(card)
              )}
            </Box>
          </Tooltip>
        </ListItem>
      </Box>
    </Fade>
  );

  return (
    <Stack spacing={1.5}>
      {cards.map((card: ModalMenu, index: number) =>
        renderMenuItem(card, index)
      )}
    </Stack>
  );
};

export default MedicalCardList;
